import { useState, useEffect } from 'react';
import { getOrderSizes } from '@/api/orderSizes';
import { getActiveSessionRevision, getActiveSessionType, getActiveSessionRoles, subscribeActiveSessionRevision } from '@/api/authenticatedClient';

// Only server-provided choices are facts. An empty/unavailable list never
// invents defaults; a transport failure preserves a previously valid list.
const EMPTY_SIZES = [];

let _cache = null;      // resolved list, cached for the session (fetch once)
let _inflight = null;   // current attempt, shared by concurrent callers
const _subscribers = new Set();   // mounted hooks, notified on refresh
let _owner = null;
let _catalogDigest; // undefined: unobserved; null: explicitly no accepted catalog
let _stopObserving = null;

const ownerKey = () => JSON.stringify([
  getActiveSessionRevision(), getActiveSessionType(),
  typeof window === 'undefined' ? null : window.lvbDesktop?.backendUrl,
]);
const localSource = () => typeof window !== 'undefined' && Boolean(window.lvbDesktop)
  && (getActiveSessionType() === 'offline'
    || window.lvbDesktop.backendUrl === window.lvbDesktop.localBackendUrl);

function adoptOwner() {
  const owner = ownerKey();
  if (_owner === owner) return false;
  _owner = owner;
  _cache = null;
  _catalogDigest = undefined;
  const retired = _inflight;
  _inflight = null;
  retired?.resolve(EMPTY_SIZES); // Never redirect an old account/source caller into a new one.
  retired?.resolveResult({ status: 'retired', sizes: EMPTY_SIZES });
  _subscribers.forEach(cb => cb(EMPTY_SIZES));
  return true;
}

function observeAuthority() {
  if (_stopObserving) return;
  let active = true;
  let statusEvents = 0;
  const acceptStatus = status => {
    if (!active || !localSource()) return;
    const absent = status?.state === 'not_available'
      && Object.prototype.hasOwnProperty.call(status, 'acceptedDigest') && status.acceptedDigest === null;
    const current = status?.state === 'current'
      && typeof status.acceptedDigest === 'string' && Boolean(status.acceptedDigest);
    // A non-admin masked status has no identity field and conveys no absence.
    if (!absent && !current) return;
    adoptOwner();
    if (_catalogDigest === status.acceptedDigest) return;
    const hadAcceptedChoices = _catalogDigest !== undefined || _cache !== null;
    _catalogDigest = status.acceptedDigest;
    // A replacement accepted generation, unlike a failed transport refresh,
    // retires the old list's authority immediately. Draft fields remain owned by forms.
    _cache = null;
    _subscribers.forEach(cb => cb(EMPTY_SIZES));
    if (current || hadAcceptedChoices) fetchSizes(true);
    else {
      const retired = _inflight;
      _inflight = null;
      retired?.resolve(EMPTY_SIZES);
      retired?.resolveResult({ status: 'retired', sizes: EMPTY_SIZES });
    }
  };
  const stopSession = subscribeActiveSessionRevision(() => {
    if (active && adoptOwner()) fetchSizes();
  });
  const bridge = typeof window === 'undefined' ? null : window.lvbDesktop;
  const stopCatalog = bridge?.onCatalogStatusChanged?.(status => {
    statusEvents += 1;
    acceptStatus(status);
  });
  const capturedOwner = ownerKey();
  const capturedEvents = statusEvents;
  Promise.resolve().then(() => bridge?.getCatalogStatus?.()).then(status => {
    if (active && ownerKey() === capturedOwner && statusEvents === capturedEvents) acceptStatus(status);
  }, () => {});
  _stopObserving = () => { active = false; stopSession(); stopCatalog?.(); _stopObserving = null; };
}

function fetchSizes(force = false, revalidate = false, request = null) {
  // Explicit management refreshes belong to the save's captured authority.
  // Refusing one must not retire another consumer's valid shared request.
  if (request && !request.isCurrent()) return Promise.resolve({ status: 'retired', sizes: EMPTY_SIZES });
  adoptOwner();
  if (!force && !revalidate && _cache) return Promise.resolve(_cache);
  if (!force && _inflight) return _inflight.promise;

  const previous = _inflight;
  const attempt = { owner: _owner };
  attempt.promise = new Promise(resolve => { attempt.resolve = resolve; });
  attempt.resultPromise = new Promise(resolve => { attempt.resolveResult = resolve; });
  _inflight = attempt;
  // Retired callers follow the replacement immediately, even if the obsolete
  // network request never settles. Further refreshes redirect the same chain.
  previous?.resolve(attempt.promise);
  previous?.resolveResult({ status: 'retired', sizes: _cache || EMPTY_SIZES });

  const current = () => ownerKey() === attempt.owner && (!request || request.isCurrent());
  const retire = () => {
    if (ownerKey() !== attempt.owner) { adoptOwner(); return; }
    if (_inflight !== attempt) return;
    _inflight = null;
    attempt.resolve(_cache || EMPTY_SIZES);
    attempt.resolveResult({ status: 'retired', sizes: _cache || EMPTY_SIZES });
  };
  const finish = (sizes, status, cacheable, clearCache = false) => {
    if (!current()) { retire(); return; }
    if (_inflight !== attempt) return;
    if (clearCache) _cache = null;
    if (cacheable) _cache = sizes;
    _inflight = null;
    _subscribers.forEach(cb => cb(sizes));
    attempt.resolve(sizes);
    attempt.resolveResult({ status, sizes });
  };
  // Keep request failures separate from subscriber delivery. Only this attempt
  // can publish or clear pending ownership; stale success/failure does neither.
  Promise.resolve().then(() => {
    if (_inflight !== attempt || !current()) { retire(); return; }
    return request ? getOrderSizes(request.config) : getOrderSizes();
  }).then(data => {
    if (_inflight !== attempt) return;
    // The explicit outcome path cannot call a malformed acknowledgement fresh.
    // Keep the legacy array-only caller's response interpretation unchanged.
    if (request && (!data || !Array.isArray(data.sizes)
      || !data.sizes.every(size => typeof size === 'string')
      || (data.available !== undefined && typeof data.available !== 'boolean')
      || (data.available === false && data.sizes.length > 0))) {
      finish(_cache || EMPTY_SIZES, 'failed', false);
    } else if (data?.available === false) finish(EMPTY_SIZES, 'unavailable', false, true);
    else finish(Array.isArray(data?.sizes) ? data.sizes : EMPTY_SIZES, 'fresh', true);
  }, () => finish(_cache || EMPTY_SIZES, 'failed', false));
  return request ? attempt.resultPromise : attempt.promise;
}

/** Refetch the size list and push it to every mounted consumer. Call after
 *  editing the sizes so all dropdowns update immediately, no reload needed. */
export async function refreshOrderSizes() {
  return fetchSizes(true);
}

/** Exact read outcome for an owner-bound management continuation. This uses the
 * same attempt and publisher as ordinary dropdown consumers; cached fallback is
 * never reported as a fresh read. Supersession retires this exact-result caller. */
export function refreshOrderSizesResult({ config, isCurrent }) {
  return fetchSizes(true, false, { config, isCurrent });
}

/** The authoritative active arrangement-size tiers, from the backend. */
export function useOrderSizes() {
  const owner = ownerKey();
  const [snapshot, setSnapshot] = useState(() => ({ owner, sizes: _owner === owner ? _cache || EMPTY_SIZES : EMPTY_SIZES }));
  useEffect(() => {
    let cancelled = false;
    adoptOwner();
    const cb = (sizes) => { if (!cancelled) setSnapshot({ owner: _owner, sizes }); };
    _subscribers.add(cb);
    observeAuthority();
    // Request completion delivers through the current attempt only, never an
    // individual stale promise callback retained by this hook.
    if (_cache) cb(_cache);
    // Catalog-status identity remains admin-only. Other local users reread on
    // mount; simultaneous mounts share the same attempt and keep valid choices
    // visible while that read is pending.
    fetchSizes(false, localSource() && !getActiveSessionRoles().includes('admin'));
    return () => {
      cancelled = true;
      _subscribers.delete(cb);
      if (!_subscribers.size) _stopObserving?.();
    };
  }, [owner]);
  return snapshot.owner === owner ? snapshot.sizes : EMPTY_SIZES;
}

export default useOrderSizes;
